import { useId } from 'react';
import { copperButton, gameColors } from '@/shared/ui/game-theme';
import type { NotificationPreferences } from '../api/notification-preferences.api';

interface Props {
  locale: string;
  preferences: NotificationPreferences | null;
  busy: boolean;
  error: 'load' | 'save' | null;
  dirty: boolean;
  onChange: (key: keyof NotificationPreferences, value: boolean) => void;
  onSave: () => void;
  onReload: () => void;
}

export function NotificationSettings({ locale, preferences, busy, error, dirty, onChange, onSave, onReload }: Props) {
  const id = useId();
  const ru = locale === 'ru';
  const toggle = (key: keyof NotificationPreferences, label: string, disabled = false) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 44, cursor: disabled || busy ? 'default' : 'pointer' }}>
      <input type="checkbox" role="switch" checked={preferences?.[key] ?? false} disabled={busy || disabled}
        onChange={event => onChange(key, event.target.checked)} style={{ width: 20, height: 20, accentColor: gameColors.copper }} />
      {label}
    </label>
  );
  return <section aria-labelledby={`${id}-title`} aria-busy={busy} style={{ marginTop: 24 }}>
    <h3 id={`${id}-title`} style={{ fontSize: 14 }}>{ru ? 'Уведомления в Telegram' : 'Telegram notifications'}</h3>
    {!preferences && busy && <p role="status">{ru ? 'Загрузка настроек…' : 'Loading preferences…'}</p>}
    {preferences && <>
      {toggle('enabled', ru ? 'Получать уведомления' : 'Receive notifications')}
      <p>{ru ? 'Общий выключатель сохраняет выбор групп. Изменения действуют после сохранения.' : 'Turning notifications off preserves your category choices. Changes apply after saving.'}</p>
      <fieldset disabled={!preferences.enabled || busy} style={{ border: 0, padding: 0, margin: '16px 0' }}>
        <legend style={{ fontWeight: 600 }}>{ru ? 'События в дороге' : 'Road events'}</legend>
        {toggle('roadAdvice', ru ? 'Нужен совет' : 'Advice needed')}
        {toggle('roadResults', ru ? 'Решения и результаты' : 'Decisions and results')}
      </fieldset>
      <fieldset disabled={!preferences.enabled || busy} style={{ border: 0, padding: 0, margin: '16px 0' }}>
        <legend style={{ fontWeight: 600 }}>{ru ? 'Другие события' : 'Other events'}</legend>
        {toggle('biography', ru ? 'Биография героя' : 'Hero biography')}
        {toggle('townArrival', ru ? 'Прибытие в город' : 'Town arrivals')}
        {toggle('questCompleted', ru ? 'Выполненные задания' : 'Completed quests')}
        {toggle('playerThoughts', ru ? 'Мысли героя об игроке' : 'Hero thoughts about you')}
      </fieldset>
      <button type="button" style={{ ...copperButton, minHeight: 44, padding: '8px 12px' }} disabled={busy || !dirty}
        onClick={onSave}>{busy ? (ru ? 'Сохранение…' : 'Saving…') : error === 'save' ? (ru ? 'Повторить сохранение' : 'Retry save') : (ru ? 'Сохранить уведомления' : 'Save notifications')}</button>
      <p role="status" aria-live="polite">{!busy && !error && (dirty ? (ru ? 'Есть несохранённые изменения.' : 'You have unsaved changes.') : (ru ? 'Настройки сохранены.' : 'Preferences saved.'))}</p>
    </>}
    {error && <p role="alert">{error === 'load' ? (ru ? 'Не удалось загрузить настройки.' : 'Could not load preferences.') : (ru ? 'Не удалось сохранить настройки. Повторите попытку.' : 'Could not save preferences. Please retry.')}</p>}
    {error === 'load' && <button type="button" style={{ ...copperButton, minHeight: 44, padding: '8px 12px' }} onClick={onReload}>{ru ? 'Загрузить снова' : 'Retry loading'}</button>}
  </section>;
}
